/**
 * Étape Travaux — Récapitulatif + équipements complémentaires.
 *
 * Cette étape arrive **après** les étapes section-spécifiques (serres,
 * élevage, stockage). Elle :
 *  1. Affiche le récap des travaux déjà sélectionnés (avec retrait possible)
 *  2. Propose les équipements complémentaires (pompage, séchage, froid, etc.)
 *     en révélant à la volée les travaux liés à chaque équipement coché
 *  3. Permet d'explorer les autres catégories non couvertes
 */

import { motion, AnimatePresence } from 'framer-motion';
import { useMemo } from 'react';
import {
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  Info,
  ListChecks,
  Sparkles,
  X,
  type LucideIcon,
} from 'lucide-react';
import { useState } from 'react';
import { Button } from '../../ui/button';
import { cn } from '../../../lib/cn';
import { easeStripe } from '../../../lib/motion';
import {
  EQUIPEMENTS,
  TRAVAUX_AGRI,
  TRAVAUX_AGRI_CATEGORIES,
  findTravauxAgri,
  type TravauxAgriCategory,
  type TravauxAgriOption,
} from './agri-config';
import { useAgriStore } from './useAgriStore';

export function Step4Travaux() {
  const equipements = useAgriStore((s) => s.equipements);
  const travaux = useAgriStore((s) => s.travaux);
  const orientation = useAgriStore((s) => s.orientation);
  const toggleEquipement = useAgriStore((s) => s.toggleEquipement);
  const toggleTravail = useAgriStore((s) => s.toggleTravail);
  const goNext = useAgriStore((s) => s.goNext);

  /* — Travaux liés aux équipements cochés (suggestions live) — */
  const equipementSuggestionIds = useMemo(() => {
    const r: string[] = [];
    for (const eqId of equipements) {
      const meta = EQUIPEMENTS.find((e) => e.id === eqId);
      if (!meta) continue;
      for (const code of meta.ceeCodes) {
        const t = TRAVAUX_AGRI.find((tr) => tr.ceeCodes.includes(code));
        if (t && !r.includes(t.id)) r.push(t.id);
      }
    }
    return r;
  }, [equipements]);

  const selectedItems = useMemo(
    () =>
      travaux
        .map((id) => findTravauxAgri(id))
        .filter((t): t is TravauxAgriOption => !!t),
    [travaux],
  );

  // Catégories à montrer en exploration (toutes celles non encore complètement traitées)
  const exploreCategories = useMemo<TravauxAgriCategory[]>(() => {
    const all = TRAVAUX_AGRI_CATEGORIES.map((c) => c.id);
    // On enlève "serres" / "elevage" / "stockage" déjà passées en étape dédiée si l'utilisateur ne s'y intéresse pas
    return all;
  }, []);

  const canContinue = true; // l'utilisateur peut continuer même sans travaux

  return (
    <div>
      <div className="flex items-center gap-2">
        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-brand-50 text-brand-700">
          <ListChecks className="h-3.5 w-3.5" strokeWidth={2} />
        </span>
        <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-brand-700">
          Récapitulatif & ajustements
        </p>
      </div>
      <h2 className="mt-2 h-section">
        Vos travaux & équipements
      </h2>
      <p className="mt-2 text-[14px] leading-relaxed text-stone-500">
        Voici les travaux que vous avez sélectionnés jusqu'ici. Vous pouvez en
        ajouter, en retirer, ou indiquer des{' '}
        <strong className="text-ink">équipements complémentaires</strong> qui
        débloqueront automatiquement de nouvelles fiches CEE.
      </p>

      {/* ── Sélection courante ────────────────────────────────── */}
      <div className="mt-6 rounded-2xl border border-stone-200 bg-white p-4">
        <div className="flex items-center justify-between gap-2">
          <p className="text-[11.5px] font-bold uppercase tracking-[0.14em] text-stone-400">
            Travaux sélectionnés
          </p>
          <span className="inline-flex h-6 min-w-[24px] items-center justify-center rounded-md bg-stone-900 px-1.5 text-[12px] font-bold tabular-nums text-white">
            {selectedItems.length}
          </span>
        </div>

        {selectedItems.length === 0 ? (
          <p className="mt-2 text-[12.5px] text-stone-500">
            Aucun travail sélectionné pour le moment. Cochez des équipements
            ci-dessous ou jetez un œil aux autres catégories.
          </p>
        ) : (
          <ul className="mt-3 space-y-1.5">
            {selectedItems.map((t) => (
              <motion.li
                key={t.id}
                layout
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 8 }}
                transition={{ duration: 0.2, ease: easeStripe }}
                className="flex items-center gap-2.5 rounded-lg bg-brand-50/50 px-2.5 py-1.5 ring-1 ring-inset ring-brand-200/50"
              >
                <CheckCircle2
                  className="h-3.5 w-3.5 shrink-0 text-brand-600"
                  strokeWidth={2.4}
                />
                <span className="min-w-0 flex-1 truncate text-[12.5px] font-medium text-ink">
                  {t.label}
                </span>
                <div className="flex flex-wrap gap-1">
                  {t.ceeCodes.map((c) => (
                    <span
                      key={c}
                      className="font-mono text-[9.5px] font-bold tracking-wider text-stone-500"
                    >
                      {c}
                    </span>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => toggleTravail(t.id)}
                  aria-label={`Retirer ${t.label}`}
                  className="ml-1 flex h-5 w-5 items-center justify-center rounded-md text-stone-400 transition-colors hover:bg-stone-200/60 hover:text-ink"
                >
                  <X className="h-3 w-3" strokeWidth={2.4} />
                </button>
              </motion.li>
            ))}
          </ul>
        )}
      </div>

      {/* ── Équipements complémentaires ───────────────────────── */}
      <div className="mt-6">
        <p className="text-[11.5px] font-bold uppercase tracking-[0.14em] text-stone-500">
          Avez-vous l'un de ces équipements&nbsp;?
        </p>
        <p className="mt-1 text-[12px] leading-relaxed text-stone-500">
          Cochez ceux que vous possédez — chaque équipement coché révèle des
          fiches CEE adaptées.
        </p>
        <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
          {EQUIPEMENTS.map((eq) => {
            const Icon = eq.icon;
            const selected = equipements.includes(eq.id);
            return (
              <button
                key={eq.id}
                type="button"
                onClick={() => toggleEquipement(eq.id)}
                className={cn(
                  'flex items-center gap-2 rounded-xl border-2 px-3 py-2 text-left transition-all duration-150',
                  selected
                    ? 'border-brand-500 bg-brand-50/70 text-brand-800'
                    : 'border-stone-200 bg-white text-stone-700 hover:border-stone-300',
                )}
              >
                <Icon
                  className={cn(
                    'h-3.5 w-3.5 shrink-0',
                    selected ? 'text-brand-700' : 'text-stone-500',
                  )}
                  strokeWidth={1.8}
                />
                <span className="truncate text-[11.5px] font-semibold">
                  {eq.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Travaux suggérés par équipement ───────────────────── */}
      <AnimatePresence>
        {equipementSuggestionIds.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.28, ease: easeStripe }}
            className="overflow-hidden"
          >
            <div className="mt-5 rounded-2xl border border-accent-200/70 bg-gradient-to-br from-accent-50/60 via-white to-brand-50/30 p-4">
              <div className="flex items-center gap-2">
                <Sparkles
                  className="h-3.5 w-3.5 text-accent-700"
                  strokeWidth={2.2}
                />
                <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-accent-900">
                  Travaux débloqués par vos équipements
                </p>
              </div>
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                {equipementSuggestionIds.map((id) => {
                  const item = findTravauxAgri(id);
                  if (!item) return null;
                  return (
                    <TravailCard
                      key={item.id}
                      item={item}
                      selected={travaux.includes(item.id)}
                      onToggle={toggleTravail}
                      compact
                    />
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Catégories à explorer (collapsibles) ──────────────── */}
      <div className="mt-7">
        <p className="text-[11.5px] font-bold uppercase tracking-[0.14em] text-stone-500">
          Explorer toutes les catégories
        </p>
        <p className="mt-1 text-[12px] leading-relaxed text-stone-500">
          {orientation
            ? `Adaptées à votre orientation "${orientation.replace('-', ' / ')}".`
            : 'Parcourez les fiches CEE secteur AGRI par thématique.'}
        </p>

        <div className="mt-3 space-y-2">
          {exploreCategories.map((catId) => (
            <CategoryAccordion key={catId} category={catId} />
          ))}
        </div>
      </div>

      <Button
        variant="brand"
        size="xl"
        disabled={!canContinue}
        onClick={goNext}
        className="mt-7 w-full gap-2"
      >
        Continuer vers mes coordonnées
        <ArrowRight className="h-4 w-4" />
      </Button>

      <p className="mt-3 flex items-center justify-center gap-1.5 text-[11px] text-stone-400">
        <Info className="h-3 w-3" strokeWidth={1.8} />
        Vous pouvez continuer même sans cocher de travaux — un conseiller
        vous proposera des pistes
      </p>
    </div>
  );
}

/* ─── Accordéon de catégorie ─────────────────────────────── */

function CategoryAccordion({ category }: { category: TravauxAgriCategory }) {
  const meta = TRAVAUX_AGRI_CATEGORIES.find((c) => c.id === category);
  const items = useMemo(
    () => TRAVAUX_AGRI.filter((t) => t.category === category),
    [category],
  );
  const travaux = useAgriStore((s) => s.travaux);
  const toggleTravail = useAgriStore((s) => s.toggleTravail);

  const selectedCount = items.filter((i) => travaux.includes(i.id)).length;
  const [open, setOpen] = useState(selectedCount > 0);

  if (!meta || items.length === 0) return null;
  const Icon: LucideIcon = meta.icon;

  return (
    <div className="overflow-hidden rounded-xl border border-stone-200 bg-white">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-3 p-3 text-left transition-colors hover:bg-stone-50/60"
      >
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-stone-100 text-stone-700">
          <Icon className="h-3.5 w-3.5" strokeWidth={1.8} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-semibold text-ink">{meta.label}</p>
          <p className="mt-0.5 truncate text-[11px] text-stone-500">
            {meta.sublabel}
          </p>
        </div>
        {selectedCount > 0 && (
          <span className="rounded-md bg-brand-100 px-2 py-0.5 text-[10.5px] font-bold tabular-nums text-brand-800">
            {selectedCount} sélectionné{selectedCount > 1 ? 's' : ''}
          </span>
        )}
        <ChevronDown
          className={cn(
            'h-4 w-4 shrink-0 text-stone-400 transition-transform duration-200',
            open && 'rotate-180',
          )}
          strokeWidth={2}
        />
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: easeStripe }}
            className="overflow-hidden"
          >
            <div className="grid gap-2 border-t border-stone-200/70 bg-stone-50/30 p-3 sm:grid-cols-2">
              {items.map((item) => (
                <TravailCard
                  key={item.id}
                  item={item}
                  selected={travaux.includes(item.id)}
                  onToggle={toggleTravail}
                  compact
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ─── Carte travail ─────────────────────────────────────── */

function TravailCard({
  item,
  selected,
  onToggle,
  compact = false,
}: {
  item: TravauxAgriOption;
  selected: boolean;
  onToggle: (id: string) => void;
  compact?: boolean;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={selected}
      onClick={() => onToggle(item.id)}
      className={cn(
        'group relative flex items-start gap-2.5 overflow-hidden rounded-xl border-2 p-3 text-left transition-all duration-150',
        selected
          ? 'border-brand-500 bg-brand-50/80 shadow-[0_4px_20px_-8px_rgba(16,185,129,0.25)]'
          : 'border-stone-200 bg-white hover:border-stone-300 hover:bg-stone-50/40',
      )}
    >
      <span
        className={cn(
          'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border-2',
          selected
            ? 'border-brand-500 bg-brand-500'
            : 'border-stone-300 bg-white group-hover:border-stone-400',
        )}
      >
        {selected && (
          <svg className="h-2.5 w-2.5 text-white" viewBox="0 0 12 10" fill="none">
            <path
              d="M1 5l3.5 3.5L11 1"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </span>

      <div className="min-w-0 flex-1">
        <p
          className={cn(
            'text-[12.5px] font-semibold leading-snug',
            selected ? 'text-brand-900' : 'text-ink',
          )}
        >
          {item.label}
        </p>
        {!compact && (
          <p className="mt-0.5 text-[11px] leading-relaxed text-stone-500">
            {item.sublabel}
          </p>
        )}
        <div className="mt-1.5 flex flex-wrap gap-1">
          {item.ceeCodes.map((code) => (
            <span
              key={code}
              className={cn(
                'inline-flex items-center rounded-md px-1.5 py-0.5 font-mono text-[9.5px] font-bold tracking-wider ring-1 ring-inset',
                selected
                  ? 'bg-accent-100 text-accent-900 ring-accent-200/60'
                  : 'bg-stone-100 text-stone-600 ring-stone-200/70',
              )}
            >
              {code}
            </span>
          ))}
        </div>
      </div>
    </button>
  );
}
